import React, { useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { differenceInDays } from 'date-fns';

import DropdownMenu, { DropdownItemRadio } from '@atlaskit/dropdown-menu';

export const i18n = defineMessages({
	dateOptionHourly: {
		id: 'confluence-analytics.period-selector.date-option-hourly',
		defaultMessage: 'Hourly',
		description: 'Title for the filter button that changes the chart display to a hourly view.',
	},
	dateOptionDaily: {
		id: 'confluence-analytics.period-selector.date-option-daily',
		defaultMessage: 'Daily',
		description: 'Title for the filter button that changes the chart display to a daily view.',
	},
	dateOptionWeekly: {
		id: 'confluence-analytics.period-selector.date-option-weekly',
		defaultMessage: 'Weekly',
		description: 'Title for the filter button that changes the chart display to a weekly view.',
	},
	dateOptionMonthly: {
		id: 'confluence-analytics.period-selector.date-option-monthly',
		defaultMessage: 'Monthly',
		description: 'Title for the filter button that changes the UI display to a monthly view.',
	},
});

export type PeriodType = 'hour' | 'day' | 'week' | 'month';

export const PeriodSelector = ({
	value,
	onChange,
	fromDate,
	toDate,
	isHourlyPeriodAllowed = false, // not allowed by default as it only works for page analytics
}: {
	value: PeriodType;
	onChange: (value: PeriodType) => void;
	fromDate: Date;
	toDate: Date;
	isHourlyPeriodAllowed?: boolean;
}) => {
	const { formatMessage } = useIntl();

	const getPeriodTriggerTitle = useCallback(
		(period: PeriodType) => {
			switch (period) {
				case 'hour':
					return formatMessage(i18n.dateOptionHourly);
				case 'day':
					return formatMessage(i18n.dateOptionDaily);
				case 'week':
					return formatMessage(i18n.dateOptionWeekly);
				case 'month':
					return formatMessage(i18n.dateOptionMonthly);
				default:
					return formatMessage(i18n.dateOptionDaily);
			}
		},
		[formatMessage],
	);

	const isHourPeriodDisabled = differenceInDays(toDate, fromDate) > 14;

	return (
		<DropdownMenu shouldRenderToParent trigger={getPeriodTriggerTitle(value)}>
			{isHourlyPeriodAllowed && (
				<DropdownItemRadio
					id="date-hourly"
					onClick={() => onChange('hour')}
					isSelected={value === 'hour'}
					isDisabled={isHourPeriodDisabled}
				>
					<FormattedMessage {...i18n.dateOptionHourly} />
				</DropdownItemRadio>
			)}
			<DropdownItemRadio
				id="date-daily"
				onClick={() => onChange('day')}
				isSelected={value === 'day'}
			>
				<FormattedMessage {...i18n.dateOptionDaily} />
			</DropdownItemRadio>
			<DropdownItemRadio
				id="date-weekly"
				onClick={() => onChange('week')}
				isSelected={value === 'week'}
			>
				<FormattedMessage {...i18n.dateOptionWeekly} />
			</DropdownItemRadio>
			<DropdownItemRadio
				id="date-monthly"
				onClick={() => onChange('month')}
				isSelected={value === 'month'}
			>
				<FormattedMessage {...i18n.dateOptionMonthly} />
			</DropdownItemRadio>
		</DropdownMenu>
	);
};
